import { useDynamicConfig as useStatsigDynamicConfig } from '@statsig/react-bindings'
import { useMemo } from 'react'
import { isVariantConfig, resolveValue } from '~/utils/flags/resolver'
import fallbackDynamicConfigs from '../../__generated__/default_configs'

type FallbackDynamicConfigs = typeof fallbackDynamicConfigs
export type DynamicConfigName = keyof FallbackDynamicConfigs
export type DynamicConfigValue<Name extends DynamicConfigName> =
  FallbackDynamicConfigs[Name]['value']

/**
 * Platform-agnostic Statsig dynamic-config hook with type-safe fallbacks and
 * country-conditional variant resolution.
 *
 * Country-code injection (rather than reading IP info inline) keeps the hook
 * pure with respect to platform: web's `useDynamicConfig` wrapper threads the
 * user's IP-derived `alpha2` from Moonpay/ipapi; React Native will thread a
 * platform-specific source.
 */
export const useDynamicConfig = <Name extends DynamicConfigName>(
  name: Name,
  options?: {
    bypassCountryCode?: boolean
    countryCode?: string
  },
): DynamicConfigValue<Name> => {
  const config = useStatsigDynamicConfig(name)
  const fallback = useMemo(() => fallbackDynamicConfigs[name], [name])
  const fallbackValue = fallback?.value

  const bypassCountryCode = !!options?.bypassCountryCode
  const countryCode = options?.countryCode

  const resolvedValue = useMemo(() => {
    if (isVariantConfig<DynamicConfigValue<Name>>(config.value)) {
      return resolveValue(
        bypassCountryCode
          ? {
              ...config.value,
              variants: config.value.variants.filter(
                (variant) => variant.condition?.type !== 'country',
              ),
            }
          : config.value,
        {
          countryCode,
        },
      )
    }

    // Not a variant config, get value directly
    return config.get('value', fallbackValue) as DynamicConfigValue<Name>
    // confirmed that config is stable (https://cdn.jsdelivr.net/npm/@statsig/react-bindings@3.30.2/src/useDynamicConfig.js)
    // but .get might not be so we memoize it ourselves
  }, [bypassCountryCode, config, fallbackValue, countryCode])

  return resolvedValue
}
